import {useCallback, useEffect} from 'react';
import {useDocumentContext} from '@/components/document/context';
import {FieldGrids} from '@/components/document/group';
import {
  GroupModalEditActions,
  GroupModalNewActions,
  GroupModalProps,
  GroupModalWrapper,
} from '@/components/document/group-modal-wrapper';
import {useGroupModalValidation} from '@/components/document/hooks/use-group-modal-validation';
import {getAction} from '@/engine/actions';
import {ACTION_TYPES} from '@/model/types/constants';

const useReturnToPreviousModal = () => {
  const {store, valueResolver, visibilitySet} = useDocumentContext();

  return useCallback(() => {
    const returnToGroupModal = store.getState().navigation.returnToPreviousModalId;
    if (!returnToGroupModal) return;

    const openPreviousModal = getAction({type: ACTION_TYPES.OPEN_MODAL, group: returnToGroupModal});
    openPreviousModal(store, valueResolver, visibilitySet)();
    store.getState().setReturnToPreviousModalId();
  }, [store, valueResolver, visibilitySet]);
};

export const ArrayGroupModal = ({group, fields, sectionId}: GroupModalProps) => {
  const {valueResolver, store, visibilitySet, documentModel, currentSectionId: currentSection} = useDocumentContext();

  const isNew = valueResolver({$ref: `_${group.id}[new]`}, {transform: value => value === 'true'});
  const returnToPreviousModal = useReturnToPreviousModal();

  const {isGroupValid, validateAllFields} = useGroupModalValidation(group.id);

  // Show errors right away when editing an existing entry; new entries only show errors once touched
  useEffect(() => {
    if (!isNew) validateAllFields();
  }, [isNew, validateAllFields]);

  const saveAndCloseModal = useCallback(() => {
    const [array, row] = group.id.split('#');
    const saveEntry = getAction({type: ACTION_TYPES.SAVE_ENTRY, array, row});

    // Execute custom onSave actions, then save the entry
    group.onSave.forEach(action => action(array, row));
    saveEntry(store, valueResolver, visibilitySet, documentModel, currentSection)();
    returnToPreviousModal();
  }, [
    store,
    valueResolver,
    visibilitySet,
    group.id,
    group.onSave,
    documentModel,
    currentSection,
    returnToPreviousModal,
  ]);

  const rollbackAndCloseModal = useCallback(() => {
    const [array, row] = group.id.split('#');
    const rollbackEntry = getAction({type: ACTION_TYPES.ROLLBACK_ENTRY_TO_DRAFT, array, row});

    // Execute custom onCancel actions, then rollback the entry
    group.onCancel.forEach(action => action(array, row));
    rollbackEntry(store, valueResolver, visibilitySet)();
    returnToPreviousModal();
  }, [store, valueResolver, visibilitySet, group.id, group.onCancel, returnToPreviousModal]);

  const deleteAndCloseModal = useCallback(() => {
    const [array, row] = group.id.split('#');
    const deleteEntry = getAction({type: ACTION_TYPES.DELETE_ENTRY, array, row});

    // Execute custom onDelete actions, then delete the entry
    group.onDelete.forEach(action => action(array, row));
    deleteEntry(store, valueResolver, visibilitySet)();
    returnToPreviousModal();
  }, [store, valueResolver, visibilitySet, group.id, group.onDelete, returnToPreviousModal]);

  const closeModalWithoutReturning = useCallback(() => {
    store.getState().setReturnToPreviousModalId();

    if (isNew) {
      deleteAndCloseModal();
    } else {
      rollbackAndCloseModal();
    }
  }, [store, isNew, deleteAndCloseModal, rollbackAndCloseModal]);

  return (
    <GroupModalWrapper group={group} onClose={closeModalWithoutReturning}>
      <FieldGrids group={group} fields={fields} sectionId={sectionId} />

      <div className="mt-5">
        {isNew ? (
          <GroupModalNewActions
            onSave={saveAndCloseModal}
            onCancel={deleteAndCloseModal}
            isSaveDisabled={!isGroupValid}
            onSaveDisabled={validateAllFields}
          />
        ) : (
          <GroupModalEditActions
            onSave={saveAndCloseModal}
            onCancel={rollbackAndCloseModal}
            onDelete={deleteAndCloseModal}
            isSaveDisabled={!isGroupValid}
            onSaveDisabled={validateAllFields}
          />
        )}
      </div>
    </GroupModalWrapper>
  );
};
